import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { useFitRecords } from './useFitRecords';
import * as FitRecords from './fitRecordStore';
import FitReport from './FitReport';
import { currentUserId } from './runCache';
import { SYSTEMS_ENABLED } from './featureFlags.js';    // Systems hidden for v1.0
import '../styles/Dashboard.css';

// ────────────────────────────────────────────────────────────────────────
// DASHBOARD REDESIGN — structure pass (client call, 23-Sep-2026)
// ────────────────────────────────────────────────────────────────────────
// This follows Madhura's Dashboard_idea.jpg mockup and the three action
// items it produced:
//   #19 Getting Started checklist + three-card layout, structure first
//   #20 first-time vs. returning-user states (no stale/irrelevant data)
//   #22 fold the old stat tiles into meaningful cards, one page, minimal
//       scroll, drop the activity feed unless it earns its place
//   #23 surface saved inputs alongside results (fit-records already
//       stores dataset_selection/optimizer_config — see fitRecordStore.js)
//   #24 run history persists across logins/systems; user's own fits are
//       shown ahead of example runs
//   #25 a folder-style "Reports" card with short summaries, not a raw
//       data dump
//
// Per the call ("build a first pass, then we'll edit content together"),
// the copy here is close to the mockup's placeholder wording — swap
// labels/microcopy freely, the DATA WIRING is the part meant to hold.
//
// REVISION (client call, dashboard items 2-5):
//   #2  compact welcome banner, shorter cards, no "Run first system" in the
//       banner, simple "Welcome back" copy
//   #3  every label is actionable (steps, card titles, metrics, feed rows);
//       progress reads "1 of 3 steps done"; Profile & settings modal so
//       organization / password can be changed later
//   #4  first-time view organised by lifecycle (Set up > Define > Run >
//       Review) with ONE run button for the whole page ("Run system", which
//       becomes "New fit" after the first saved run)
//   #5  dark theme + green accent — see the "DARK THEME" block at the end
//       of Dashboard.css
// ────────────────────────────────────────────────────────────────────────

const roleConfig = {
  Admin: {
    label: 'Admin',
    color: 'danger',
    icon: 'ti-shield',
    description: 'Full system access with global configuration privileges.',
    capabilities: [
      { icon: 'ti-users', text: 'View and manage all users' },
      { icon: 'ti-settings', text: 'Manage global simulation settings' },
      { icon: 'ti-folder', text: 'Oversee all projects' },
    ],
    quickActions: [
      { icon: 'ti-users', label: 'Manage users', sub: 'Accounts', color: 'danger' },
      { icon: 'ti-settings', label: 'Global settings', sub: 'Config', color: 'secondary' },
      { icon: 'ti-chart-bar', label: 'System metrics', sub: 'Overview', color: 'info' },
      { icon: 'ti-file-export', label: 'Export logs', sub: 'CSV / JSON', color: 'secondary' },
    ],
  },
  BusinessUser: {
    label: 'Business User',
    color: 'info',
    icon: 'ti-briefcase',
    description: 'Project management access with team oversight capabilities.',
    capabilities: [
      { icon: 'ti-upload', text: 'Create and upload simulation projects' },
      { icon: 'ti-chart-bar', text: 'Review simulation results' },
      { icon: 'ti-trending-up', text: 'Track progress of teams' },
    ],
    quickActions: [
      { icon: 'ti-folder-plus', label: 'New project', sub: 'Create', color: 'info' },
      { icon: 'ti-chart-line', label: 'View results', sub: 'All projects', color: 'success' },
      { icon: 'ti-users', label: 'Team activity', sub: 'Overview', color: 'secondary' },
      { icon: 'ti-download', label: 'Export report', sub: 'CSV / PDF', color: 'secondary' },
    ],
  },
  AssociateResearcher: {
    label: 'Associate Researcher',
    color: 'success',
    icon: 'ti-microscope',
    description: 'Research-focused access for running and analyzing simulations.',
    capabilities: [
      { icon: 'ti-upload', text: 'Upload input files (.csv)' },
      { icon: 'ti-player-play', text: 'Run KinetiQ simulations' },
      { icon: 'ti-chart-line', text: 'View plots and download results' },
    ],
    quickActions: [
      { icon: 'ti-upload', label: 'Upload CSV', sub: 'Input file', color: 'info' },
      { icon: 'ti-player-play', label: 'Run simulation', sub: 'KinetiQ', color: 'success' },
      { icon: 'ti-chart-line', label: 'View plots', sub: 'Latest results', color: 'warning' },
      { icon: 'ti-download', label: 'Export results', sub: 'CSV / PDF', color: 'secondary' },
    ],
  }};

// Backend cap on saved fit runs per system — mirrors MAX_COMPARE-style
// server constants elsewhere. Example runs are NOT counted against this
// (per action item #24); update alongside the backend if that changes.
const MAX_SAVED_FITS = 10;
const MAX_EXAMPLE_RUNS = 5;   // example runs kept/shown separately, not counted in the 10

// Cards the user can show/hide from "Customize" (item #22, "configurable cards").
const CARD_DEFS = [
  { key: 'activity',        label: 'System activity & alerts' },
  { key: 'reactionSystems', label: 'Reaction Systems' },
  { key: 'reports',         label: 'Reports & data exports' },
  { key: 'metrics',         label: 'Performance metrics' },
];
const DEFAULT_CARDS = { activity: false, reactionSystems: true, reports: true, metrics: true };
const DEFAULT_CARD_KEYS = new Set(Object.keys(DEFAULT_CARDS));
// Systems hidden for v1.0 (featureFlags.js): drop its card from Customize too.
const AVAILABLE_CARD_DEFS = CARD_DEFS.filter((c) => SYSTEMS_ENABLED || c.key !== 'reactionSystems');

const statusMeta = {
  converged: { pill: 'pill-done', label: 'Converged' },
  stopped:   { pill: 'pill-wait', label: 'Stopped' },
  failed:    { pill: 'pill-run',  label: 'Running' },
};

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}

function shortDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState('');

  // ── Saved fitting/optimization run history ──────────────────────────
  // BUG FIX: previously, resolving which reaction_system a user's saved
  // fits belong to relied on a project_id cached in localStorage
  // (fitRecordStore.js). If that cache was ever lost — e.g. logout
  // clearing localStorage, or a different account on the same browser —
  // a fresh project+reaction_system got auto-created and every earlier
  // fit_records row became unreachable from the UI, even though it was
  // never deleted from the database. GET /api/projects/default now
  // resolves this idempotently from the authenticated user's token
  // instead, so it's stable across logout/login and across browsers.
  const fitRecordState = useFitRecords();
  const { fits, allFits, allFitsScope, error: fitsError } = fitRecordState;
  // Runs across ALL the user's reaction systems (item #24) once the backend
  // serves GET /fit-records?scope=all; until then this is the current system.
  const runs = allFitsScope === 'all' ? allFits : fits;
  const uid = useMemo(() => currentUserId(), []);
  const [viewingFit, setViewingFit] = useState(null);   // fit_uuid currently expanded
  const [fitDetail, setFitDetail] = useState(null);     // full record for viewingFit
  const [detailLoading, setDetailLoading] = useState(false);

  // Step 1 of the checklist: a real, user-triggered action — NOT auto-done.
  // Placeholder profile (organization only), stored locally per user until
  // Madhura defines what "account setup" should contain and the backend
  // has a profile endpoint.
  const profileKey = `kq_profile:${uid}`;
  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem(profileKey)); } catch (e) { return null; }
  });
  const [showProfile, setShowProfile] = useState(false);
  const [orgDraft, setOrgDraft] = useState((profile && profile.organization) || '');
  const [savedNote, setSavedNote] = useState(false);

  // Step 2: has the user actually entered something for this system?
  const [hasSavedInputs, setHasSavedInputs] = useState(false);

  // Customize cards (per user, remembered across visits).
  const cardsKey = `kq_dash_cards:${uid}`;
  const [visibleCards, setVisibleCards] = useState(() => {
    try { const stored = JSON.parse(localStorage.getItem(cardsKey)) || {}; const cleaned = Object.fromEntries(Object.entries(stored).filter(([k]) => DEFAULT_CARD_KEYS.has(k))); return { ...DEFAULT_CARDS, ...cleaned }; } catch (e) { return DEFAULT_CARDS; }
  });
  const [showCustomize, setShowCustomize] = useState(false);

  useEffect(() => {
    const storedRole = localStorage.getItem('role');
    if (!storedRole) {
      navigate('/');
    } else {
      setRole(storedRole);
    }
  }, [navigate]);

  useEffect(() => {
    if (!role) return;
    FitRecords.bootstrapFitRecords();
  }, [role]);

  // Does this system have any saved form state / last run yet? Drives step 2.
  useEffect(() => {
    const id = fitRecordState.reactionSystemId;
    if (!id) return undefined;
    let cancelled = false;
    FitRecords.loadSessionState(id).then((s) => {
      if (cancelled || !s) return;
      const fs = s.form_state;
      const filled = fs && typeof fs === 'object' && Object.keys(fs).length > 0;
      setHasSavedInputs(Boolean(filled || s.last_run_result));
    });
    return () => { cancelled = true; };
  }, [fitRecordState.reactionSystemId]);

  const openProfile = () => {
    setOrgDraft((profile && profile.organization) || '');
    setSavedNote(false);
    setShowProfile(true);
  };

  // Works for first-time setup AND later edits (client item #3).
  const saveProfile = () => {
    const p = { ...(profile || {}), organization: orgDraft.trim(), completed_at: (profile && profile.completed_at) || new Date().toISOString(), updated_at: new Date().toISOString() };
    try { localStorage.setItem(profileKey, JSON.stringify(p)); } catch (e) { /* storage blocked: still mark done for this session */ }
    setProfile(p);
    setSavedNote(true);
    if (!profile) setShowProfile(false);
  };

  const toggleCard = (key) => {
    setVisibleCards((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!Object.values(next).some(Boolean)) return prev;   // never hide every card
      try { localStorage.setItem(cardsKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  };

  const handleViewFit = async (fitUuid) => {
    if (viewingFit === fitUuid) {
      setViewingFit(null);
      setFitDetail(null);
      return;
    }
    setViewingFit(fitUuid);
    setFitDetail(null);
    setDetailLoading(true);
    const detail = await FitRecords.fetchFitDetail(fitUuid);
    setFitDetail(detail);
    setDetailLoading(false);
  };

  // "Reopen" = make it the active fit AND stage its saved inputs so
  // DataFittingPage can refill the form (item #23). Runs saved before
  // input snapshots existed reopen the fit only.
  const handleOpenInOptimize = async (fitUuid) => {
    await FitRecords.setActiveFit(fitUuid);
    await FitRecords.stageRestore(fitUuid);
    navigate('/data-fitting#optimize');
  };

  const cfg = roleConfig[role] || null;

  // ── Getting Started checklist state ──────────────────────────────────
  // Every step reflects something the USER did — a brand-new account must
  // start at 0% (per the mockup). An earlier version treated step 2 as done
  // as soon as a reaction system existed, but one is auto-created on first
  // load, so new users wrongly started at 67%.
  //   1. profile saved (Start Setup)
  //   2. entered inputs for a system, or already has a run
  //   3. has at least one saved run
  const steps = useMemo(() => ([
    {
      key: 'account',
      label: 'Set up your account',
      desc: 'Add your organization so your account details are complete.',
      done: Boolean(profile),
      onClick: openProfile,
    },
    {
      key: 'data',
      label: 'Define your reaction system',
      desc: 'Enter reactions and reactor conditions, then upload your data.',
      done: hasSavedInputs || runs.length > 0,
      onClick: () => navigate('/data-fitting#input'),
    },
    {
      key: 'run',
      label: 'Run your first fit',
      desc: 'Fit a model. Plots and reports will appear on this page.',
      done: runs.length > 0,
      onClick: () => navigate('/data-fitting#input'),
    },
  ]), [profile, hasSavedInputs, runs.length, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const completedCount = steps.filter((s) => s.done).length;
  const currentStepIndex = steps.findIndex((s) => !s.done);
  const isFirstTimeUser = runs.length === 0;
  // Onboarding is for first-time users only. Once there is a saved run the
  // checklist goes away for good — an existing user shouldn't keep seeing
  // "getting started" because an optional profile step is still open.
  const showChecklist = runs.length === 0;

  // ── Run history, prioritized ─────────────────────────────────────────
  // User's own fits first (newest, max 10), example runs after (max 5, not
  // counted in the 10) — item #24. See selectVisibleRuns() in the store.
  const visible = useMemo(
    () => FitRecords.selectVisibleRuns(runs, { maxUser: MAX_SAVED_FITS, maxExample: MAX_EXAMPLE_RUNS }),
    [runs]
  );
  const prioritizedFits = useMemo(() => [...visible.own, ...visible.examples], [visible]);
  const sysName = (f) => f.system_name || f.reaction_system_name || null;
  const multiSystem = new Set(prioritizedFits.map(sysName).filter(Boolean)).size > 1;

  const hasRuns = runs.length > 0;

  // ── Performance metrics card ─────────────────────────────────────────
  // Counts EVERY saved run (own + example runs) so the numbers match the
  // Reports page total. Stopped runs from built-in example systems are saved
  // with is_example = true, so counting only own fits hid them.
  // Only the 10-run history CAPACITY is measured on the user's own fits
  // (example runs are not counted against it — item #24).
  const userRunCount = visible.ownTotal;
  const totalRunCount = runs.length;
  const outcomes = useMemo(() => {
    let converged = 0;
    let stopped = 0;
    runs.forEach((r) => {
      if (r.status === 'converged') converged += 1;
      else if (r.status === 'stopped') stopped += 1;
    });
    return { converged, stopped, other: Math.max(runs.length - converged - stopped, 0) };
  }, [runs]);
  const convergedPct = totalRunCount > 0 ? Math.round((outcomes.converged / totalRunCount) * 100) : null;
  const capacityUsed = Math.min(userRunCount, MAX_SAVED_FITS);
  const segPct = (n) => (totalRunCount > 0 ? (n / totalRunCount) * 100 : 0);
  const feedDot = (s) => (s === 'converged' ? 'dot-done' : s === 'stopped' ? 'dot-warn' : 'dot-run');
  const feedPill = (s) => (s === 'converged' ? 'pill-done' : s === 'stopped' ? 'pill-wait' : 'pill-run');

  const runLabel = hasRuns ? 'New fit' : 'Run system';

  return (
    <div style={styles.shell}>
      <Sidebar />

      <div style={styles.rightCol}>
        <Navbar activePage="app" />

        <div style={styles.pageContent}>
          <div className="dash-page">

            {/* ── Welcome banner (compact; no run button — item #2) ── */}
            <div className="dash-hero">
              <h1 className="dash-hero-title">
                {isFirstTimeUser
                  ? <>Welcome to KinetiQ – <em>set up your first system below</em></>
                  : <>Welcome back – <em>pick up where you left off or start a new fit</em></>}
              </h1>
            </div>

            {/* ── Getting started: lifecycle steps, each one a link ── */}
            {showChecklist && (
              <div className="dash-checklist-card">
                <div className="dash-checklist-head">
                  <p className="dash-checklist-title">Getting started</p>
                  <span className="dash-progress-label" title="Each step turns green once you complete it">
                    {completedCount} of {steps.length} steps done
                  </span>
                </div>
                <div className="dash-checklist-steps">
                  {steps.map((step, i) => (
                    <button
                      type="button"
                      key={step.key}
                      onClick={step.onClick}
                      className={`dash-step-link ${step.done ? 'dash-step--done' : ''} ${i === currentStepIndex ? 'dash-step--current' : ''}`}
                    >
                      <span className="dash-step-num">
                        {step.done ? <i className="ti ti-check" aria-hidden="true" /> : i + 1}
                      </span>
                      <span className="dash-step-text">
                        <span className="dash-step-label">
                          {step.label}
                          {i === currentStepIndex && <span className="dash-step-tag">Current step</span>}
                          {!step.done && i !== currentStepIndex && <span className="dash-step-pending">Pending</span>}
                        </span>
                        <span className="dash-step-desc">{step.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick-action menu (Settings, Examples, Reaction system): shown to ALL users.
                It used to render only while hasRuns was false, so a returning user saw it flash
                until their saved runs finished loading and then it vanished. */}
            <div className="dash-summary-grid dash-first-grid" style={{ '--dash-cols': 3 }}>
              <div className="dash-summary-card dash-first-card">
                <i className="ti ti-settings dash-first-icon" aria-hidden="true" />
                <div className="dash-first-card-body">
                  <h3 className="dash-first-card-title">Account Settings</h3>
                  <p className="dash-summary-text">Edit your settings and define your preferences.</p>
                  <button type="button" className="dash-first-card-btn" style={{ cursor: 'pointer' }} onClick={() => navigate('/edit-account')}>Go to Settings</button>
                </div>
              </div>
              <div className="dash-summary-card dash-first-card">
                <i className="ti ti-book dash-first-icon" aria-hidden="true" />
                <div className="dash-first-card-body">
                  <h3 className="dash-first-card-title">Practice Examples</h3>
                  <p className="dash-summary-text">Practice using literature examples.</p>
                  <button type="button" className="dash-first-card-btn" style={{ cursor: 'pointer' }} onClick={() => navigate('/examples')}>Examples</button>
                </div>
              </div>
              <div className="dash-summary-card dash-first-card">
                <i className="ti ti-chart-line dash-first-icon" aria-hidden="true" />
                <div className="dash-first-card-body">
                  <h3 className="dash-first-card-title">Reaction system</h3>
                  <p className="dash-summary-text">Enter your reaction system input to generate the reaction rate model.</p>
                  <button type="button" className="dash-first-card-btn" style={{ cursor: 'pointer' }} onClick={() => navigate('/data-fitting#input')}>Data Fitting Input</button>
                </div>
              </div>
            </div>

            {/* ── Overview/history appears only after the first saved run ── */}
            {hasRuns && <div className="dash-section-bar">
              <span className="dash-section-title">Overview</span>
              <div className="dash-section-actions">
                {hasRuns && (
                  <div className="dash-customize">
                    <button className="dash-view-all" onClick={() => setShowCustomize((v) => !v)}>
                      <i className="ti ti-adjustments" aria-hidden="true" /> Customize
                    </button>
                    {showCustomize && (
                      <div className="dash-customize-menu">
                        {AVAILABLE_CARD_DEFS.map((c) => (
                          <label key={c.key} className="dash-customize-item">
                            <input type="checkbox" checked={visibleCards[c.key]} onChange={() => toggleCard(c.key)} />
                            {c.label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <button className="dash-summary-cta dash-run-btn" onClick={() => navigate('/data-fitting#input')}>
                  <i className="ti ti-player-play" aria-hidden="true" /> {runLabel}
                </button>
              </div>
            </div>}


            {hasRuns && (
            <div
              className="dash-summary-grid"
              style={{ '--dash-cols': Math.max(AVAILABLE_CARD_DEFS.filter((c) => visibleCards[c.key]).length, 1) }}
            >

              {/* Card 1 — Activity (rows open the run summary) */}
              {visibleCards.activity && (
              <div className="dash-summary-card dash-summary-card--data dash-summary-card--scroll">
                <div className="dash-summary-card-header">
                  <button type="button" className="dash-summary-card-title dash-title-link" onClick={() => navigate('/run-history')}>
                    <i className="ti ti-activity" aria-hidden="true" /> System activity &amp; alerts
                  </button>
                  <span className="dash-count">{prioritizedFits.length}</span>
                </div>
                <div className="dash-card-scroll">
                  {prioritizedFits.map((f) => (
                    <button
                      type="button"
                      key={f.fit_uuid}
                      className={`dash-feed-row dash-feed-row--btn ${viewingFit === f.fit_uuid ? 'dash-feed-row--open' : ''}`}
                      onClick={() => handleViewFit(f.fit_uuid)}
                    >
                      <span className={`dash-feed-dot ${feedDot(f.status)}`} />
                      <span className="dash-feed-main">
                        <span className="dash-feed-name">
                          {f.label || 'Untitled fit'}
                          {f.is_example && <span className="dash-example-tag">Example</span>}
                        </span>
                        <span className="dash-feed-when">{shortDate(f.created_at)}{multiSystem && sysName(f) ? ` · ${sysName(f)}` : ''}</span>
                      </span>
                      <span className={`dash-status-pill ${feedPill(f.status)}`}>
                        {(statusMeta[f.status] || {}).label || f.status}
                      </span>
                    </button>
                  ))}
                </div>
                {fitsError && <p className="dash-summary-error">Couldn't refresh activity: {fitsError}</p>}
              </div>
              )}

              {/* Card 2 — Reaction Systems */}
              {SYSTEMS_ENABLED && visibleCards.reactionSystems && (
              <div className="dash-summary-card dash-summary-card--data dash-summary-card--scroll">
                <div className="dash-summary-card-header">
                  <button type="button" className="dash-summary-card-title dash-title-link" onClick={() => navigate('/systems')}>
                    <i className="ti ti-molecule" aria-hidden="true" /> Reaction Systems
                  </button>
                  <button className="dash-view-all" onClick={() => navigate('/systems')}>View all</button>
                </div>
                <div className="dash-card-scroll">
                  {(() => {
                    const groups = [];
                    const byKey = new Map();
                    for (const f of prioritizedFits) {
                      const key = f.system_uuid || f.system_name || f.reaction_system_name || 'Current system';
                      let g = byKey.get(key);
                      if (!g) { g = { key, name: f.system_name || f.reaction_system_name || 'Current system', count: 0, latest: f.created_at }; byKey.set(key, g); groups.push(g); }
                      g.count += 1;
                      if (!g.latest || (f.created_at && new Date(f.created_at) > new Date(g.latest))) g.latest = f.created_at;
                    }
                    return groups.slice(0, 8).map((g) => (
                      <button type="button" key={g.key} className="dash-feed-row dash-feed-row--btn" onClick={() => navigate('/run-history')}>
                        <span className="dash-feed-dot dot-done" />
                        <span className="dash-feed-main">
                          <span className="dash-feed-name">{g.name}</span>
                          <span className="dash-feed-when">{g.count} saved run{g.count === 1 ? '' : 's'} · Last {shortDay(g.latest)}</span>
                        </span>
                      </button>
                    ));
                  })()}
                </div>
              </div>
              )}

              {/* Card 3 — Reports (folder rows) */}
              {visibleCards.reports && (
              <div className="dash-summary-card dash-summary-card--data dash-summary-card--scroll">
                <div className="dash-summary-card-header">
                  <button type="button" className="dash-summary-card-title dash-title-link" onClick={() => navigate('/run-history')}>
                    <i className="ti ti-folder" aria-hidden="true" /> Reports &amp; data exports
                  </button>
                  <button className="dash-view-all" onClick={() => navigate('/run-history')}>View all</button>
                </div>
                <div className="dash-card-scroll">
                  {prioritizedFits.map((f) => (
                    <button
                      key={f.fit_uuid}
                      className={`dash-folder-row ${viewingFit === f.fit_uuid ? 'dash-folder-row--open' : ''}`}
                      onClick={() => handleViewFit(f.fit_uuid)}
                    >
                      <span className="dash-folder-iconbox">
                        <i className="ti ti-folder" aria-hidden="true" />
                      </span>
                      <span className="dash-folder-name">{f.label || 'Untitled run'}</span>
                      <span className="dash-folder-meta">
                        {f.model_type ? (f.model_type === 'lhhw' ? 'LHHW' : 'Power Law') : shortDay(f.created_at)}
                      </span>
                      <i className={`ti ti-chevron-${viewingFit === f.fit_uuid ? 'up' : 'down'} dash-folder-chevron`} aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Card 4 — Performance metrics (saved-run count, outcomes, capacity).
                  Uses the existing .dash-perf / .dash-metric / .dash-stack /
                  .dash-legend / .dash-meter styles in Dashboard.css. */}
              {visibleCards.metrics && (
              <div className="dash-summary-card dash-summary-card--data">
                <div className="dash-summary-card-header">
                  <span className="dash-summary-card-title">
                    <i className="ti ti-chart-bar" aria-hidden="true" /> Performance metrics
                  </span>
                </div>
                <div className="dash-perf">
                  <div className="dash-metric-grid">
                    <button type="button" className="dash-metric" onClick={() => navigate('/run-history')} title={`${userRunCount} of your own fits + ${totalRunCount - userRunCount} example runs`}>
                      <span className="dash-metric-val">{totalRunCount}</span>
                      <span className="dash-metric-lbl">Saved runs</span>
                    </button>
                    <button type="button" className="dash-metric" onClick={() => navigate('/run-history')} title="Open reports">
                      <span className={`dash-metric-val ${convergedPct != null ? 'dash-metric-val--good' : 'dash-metric-val--muted'}`}>
                        {convergedPct != null ? `${convergedPct}%` : '\u2014'}
                      </span>
                      <span className="dash-metric-lbl">Converged</span>
                    </button>
                  </div>

                  <div className="dash-perf-block">
                    <p className="dash-perf-label" style={{ margin: 0 }}>
                      <span>Run outcomes</span><span>{totalRunCount} total</span>
                    </p>
                    <div className="dash-stack" role="img"
                      aria-label={`${outcomes.converged} converged, ${outcomes.stopped} stopped, ${outcomes.other} other`}>
                      <span className="dash-stack-seg seg-ok"   style={{ width: `${segPct(outcomes.converged)}%` }} />
                      <span className="dash-stack-seg seg-warn" style={{ width: `${segPct(outcomes.stopped)}%` }} />
                      <span className="dash-stack-seg seg-bad"  style={{ width: `${segPct(outcomes.other)}%` }} />
                    </div>
                    <div className="dash-legend">
                      <span><i className="dash-legend-dot seg-ok" />Converged {outcomes.converged}</span>
                      <span><i className="dash-legend-dot seg-warn" />Stopped {outcomes.stopped}</span>
                      <span><i className="dash-legend-dot seg-bad" />Other {outcomes.other}</span>
                    </div>
                  </div>

                  <div className="dash-perf-block">
                    <p className="dash-perf-label" style={{ margin: 0 }}>
                      <span>Run history capacity</span><span>{capacityUsed} / {MAX_SAVED_FITS}</span>
                    </p>
                    <div className="dash-meter">
                      <div className="dash-meter-fill" style={{ width: `${(capacityUsed / MAX_SAVED_FITS) * 100}%` }} />
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
            )}

            {/* Expanded "Summary of system run" (short fact list, per #25) */}
            {hasRuns && viewingFit && (
              <div className="dash-run-summary">
                <div className="dash-panel-header">
                  <p className="dash-panel-title">Summary of system run</p>
                  <button className="dash-view-all" onClick={() => handleViewFit(viewingFit)}>Close</button>
                </div>
                {detailLoading && <p className="dash-summary-text">Loading run detail…</p>}
                {!detailLoading && fitDetail && (
                  <div className="dash-run-summary-grid">
                    <div><span className="dash-td-label">Model</span>{fitDetail.model_type === 'lhhw' ? 'LHHW' : 'Power Law'} · {fitDetail.reversibility === 'irreversible' ? 'Irreversible' : 'Reversible'}</div>
                    <div><span className="dash-td-label">Exponent mode</span>{fitDetail.exponent_mode || '—'}</div>
                    <div><span className="dash-td-label">Datasets used</span>{Array.isArray(fitDetail.dataset_selection) ? fitDetail.dataset_selection.length : (fitDetail.dataset_selection ? Object.keys(fitDetail.dataset_selection).length : '—')}</div>
                    <div><span className="dash-td-label">Fitted parameters</span>{fitDetail.fitted_parameters ? Object.keys(fitDetail.fitted_parameters).length : 0}</div>
                    <div><span className="dash-td-label">Saved inputs</span>{fitDetail.input_snapshot ? 'Stored with this run' : 'Not stored (older run)'}</div>
                    {fitDetail.notes && <div className="dash-run-summary-notes"><span className="dash-td-label">Notes</span>{fitDetail.notes}</div>}
                  </div>
                )}
                {!detailLoading && !fitDetail && <p className="dash-summary-error">Couldn't load this run's detail.</p>}
                {!detailLoading && fitDetail && <div className="dash-readable-report"><FitReport detail={fitDetail} loading={false} /></div>}
                <div className="dash-run-summary-actions">
                  <button className="dash-view-all" onClick={() => handleOpenInOptimize(viewingFit)}>Reopen with saved inputs</button>
                  <button className="dash-view-all" onClick={() => navigate('/run-history')} style={{ marginLeft: 16 }}>Full run history</button>
                </div>
              </div>
            )}

            {/* ── Profile & settings (item #3): first-time setup and later edits ── */}
            {showProfile && (
              <div className="dash-modal-backdrop" onClick={() => setShowProfile(false)}>
                <div className="dash-modal" role="dialog" aria-modal="true" aria-label="Profile and settings" onClick={(e) => e.stopPropagation()}>
                  <p className="dash-modal-title">Profile &amp; settings</p>
                  <p className="dash-modal-text">Update your account details at any time.</p>

                  {cfg && (
                    <div className="dash-modal-row">
                      <span className="dash-modal-label">Role</span>
                      <span className="dash-modal-value"><i className={`ti ${cfg.icon}`} aria-hidden="true" /> {cfg.label}</span>
                    </div>
                  )}

                  <label className="dash-modal-label" htmlFor="kq-org">Organization / lab</label>
                  <input
                    id="kq-org"
                    className="dash-modal-input"
                    value={orgDraft}
                    onChange={(e) => { setOrgDraft(e.target.value); setSavedNote(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && orgDraft.trim()) saveProfile(); }}
                    placeholder="e.g. Vidya Yug Labs"
                    autoFocus
                  />

                  <div className="dash-modal-row dash-modal-row--split">
                    <span className="dash-modal-label">Password</span>
                    <button type="button" className="dash-view-all" onClick={() => navigate('/edit-account')}>Change password</button>
                  </div>

                  <div className="dash-modal-actions">
                    {savedNote && <span className="dash-modal-saved">Saved</span>}
                    <button className="dash-view-all" onClick={() => setShowProfile(false)}>Close</button>
                    <button className="dash-summary-cta" onClick={saveProfile} disabled={!orgDraft.trim()}>Save changes</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  /* Full-viewport flex row — must NOT scroll */
  shell: {
    display:  'flex',
    height:   '100vh',
    overflow: 'hidden',
    width:    '100%',
  },
  /* Right column: sits beside the 220px sidebar */
  rightCol: {
    marginLeft:    '220px',
    width:         'calc(100% - 220px)',
    display:       'flex',
    flexDirection: 'column',
    height:        '100vh',
    overflow:      'hidden',
  },
  /* Scrollable content area below the 68px fixed Navbar */
  pageContent: {
    flex:      1,
    marginTop: '68px',       /* clears the fixed Navbar — unified height */
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
  },
};

export default Dashboard;